/**
 * Jobsites React Query Hooks
 * Provides data fetching hooks for jobsite/project components
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as jobsitesApi from '../services/api/jobsites';
import logger from '../utils/logger';

// Query keys
export const JOBSITES_KEYS = {
  all: ['jobsites'],
  list: (params) => [...JOBSITES_KEYS.all, 'list', params],
  detail: (id) => [...JOBSITES_KEYS.all, 'detail', id],
  summary: () => [...JOBSITES_KEYS.all, 'summary'],
  workers: (id) => [...JOBSITES_KEYS.all, 'workers', id],
  safety: (id) => [...JOBSITES_KEYS.all, 'safety', id],
  compliance: (id) => [...JOBSITES_KEYS.all, 'compliance', id],
  activity: (id) => [...JOBSITES_KEYS.all, 'activity', id],
  notifications: (id) => [...JOBSITES_KEYS.all, 'notifications', id],
  contractors: (id) => [...JOBSITES_KEYS.all, 'contractors', id],
  documents: (id) => [...JOBSITES_KEYS.all, 'documents', id],
  photos: (id) => [...JOBSITES_KEYS.all, 'photos', id],
  messages: (id) => [...JOBSITES_KEYS.all, 'messages', id],
  crews: (id) => [...JOBSITES_KEYS.all, 'crews', id],
  suggestions: () => [...JOBSITES_KEYS.all, 'suggestions'],
  upcomingItems: (id, opts = {}) => [...JOBSITES_KEYS.all, 'upcoming-items', id, opts],
};

/**
 * Hook to fetch all jobsites
 */
export function useJobsites(params = {}, options = {}) {
  return useQuery({
    queryKey: JOBSITES_KEYS.list(params),
    queryFn: () => jobsitesApi.fetchJobsites(params),
    staleTime: 60000,
    retry: 1,
    ...options,
  });
}

/**
 * Hook to fetch a single jobsite
 */
export function useJobsite(jobsiteId, options = {}) {
  return useQuery({
    queryKey: JOBSITES_KEYS.detail(jobsiteId),
    queryFn: () => jobsitesApi.fetchJobsite(jobsiteId),
    enabled: !!jobsiteId,
    staleTime: 60000,
    ...options,
  });
}

/**
 * Composed upcoming items (meetings, permit expiries, overdue items, etc.)
 */
export function useJobsiteUpcomingItems(jobsiteId, opts = {}, options = {}) {
  return useQuery({
    queryKey: JOBSITES_KEYS.upcomingItems(jobsiteId, opts),
    queryFn: () => jobsitesApi.fetchJobsiteUpcomingItems(jobsiteId, { limit: opts.limit ?? 20 }),
    enabled: !!jobsiteId,
    staleTime: 60_000,
    ...options,
  });
}

/**
 * Hook to fetch jobsite summary for dashboard
 * Returns aggregated statistics: counts, status distribution,
 * worker distribution, and upcoming deadlines
 */
export function useJobsiteSummary(options = {}) {
  return useQuery({
    queryKey: JOBSITES_KEYS.summary(),
    queryFn: jobsitesApi.fetchJobsiteSummary,
    staleTime: 60000, // 1 minute
    ...options,
  });
}

/**
 * Hook to create a new jobsite
 */
export function useCreateJobsite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => jobsitesApi.createJobsite(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: JOBSITES_KEYS.all });
      logger.info('[useJobsites] Jobsite created successfully');
    },
    onError: (error) => {
      logger.error('[useJobsites] Create jobsite failed', { error: error.message });
    },
  });
}

/**
 * Hook to update a jobsite
 */
export function useUpdateJobsite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ jobsiteId, data }) => jobsitesApi.updateJobsite(jobsiteId, data),
    onSuccess: (_, { jobsiteId }) => {
      queryClient.invalidateQueries({ queryKey: JOBSITES_KEYS.detail(jobsiteId) });
      queryClient.invalidateQueries({ queryKey: JOBSITES_KEYS.all });
      logger.info('[useJobsites] Jobsite updated successfully');
    },
    onError: (error) => {
      logger.error('[useJobsites] Update jobsite failed', { error: error.message });
    },
  });
}

/**
 * Hook to delete a jobsite
 */
export function useDeleteJobsite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobsiteId) => jobsitesApi.deleteJobsite(jobsiteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: JOBSITES_KEYS.all });
      logger.info('[useJobsites] Jobsite deleted successfully');
    },
    onError: (error) => {
      logger.error('[useJobsites] Delete jobsite failed', { error: error.message });
    },
  });
}

/**
 * Hook to fetch workers for a jobsite
 */
export function useJobsiteWorkers(jobsiteId, options = {}) {
  return useQuery({
    queryKey: JOBSITES_KEYS.workers(jobsiteId),
    queryFn: () => jobsitesApi.fetchJobsiteWorkers(jobsiteId),
    enabled: !!jobsiteId,
    staleTime: 60000,
    ...options,
  });
}

/**
 * Hook to acknowledge a jobsite attention alert
 */
export function useAcknowledgeJobsiteAlert(jobsiteId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (alertId) => jobsitesApi.acknowledgeJobsiteAlert(jobsiteId, alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: JOBSITES_KEYS.safety(jobsiteId) });
      logger.info('[useJobsites] Jobsite alert acknowledged', { jobsiteId });
    },
    onError: (error) => {
      logger.error('[useJobsites] Acknowledge jobsite alert failed', {
        jobsiteId,
        error: error.message,
      });
    },
  });
}

/**
 * Hook to fetch jobsite safety data
 */
export function useJobsiteSafety(jobsiteId, options = {}) {
  return useQuery({
    queryKey: JOBSITES_KEYS.safety(jobsiteId),
    queryFn: () => jobsitesApi.fetchJobsiteSafety(jobsiteId),
    enabled: !!jobsiteId,
    staleTime: 60000,
    ...options,
  });
}

/**
 * Hook to fetch jobsite compliance data
 */
export function useJobsiteCompliance(jobsiteId, options = {}) {
  return useQuery({
    queryKey: JOBSITES_KEYS.compliance(jobsiteId),
    queryFn: () => jobsitesApi.fetchJobsiteCompliance(jobsiteId),
    enabled: !!jobsiteId,
    staleTime: 60000,
    ...options,
  });
}

/**
 * Hook to fetch jobsite activity feed
 */
export function useJobsiteActivity(jobsiteId, options = {}) {
  return useQuery({
    queryKey: JOBSITES_KEYS.activity(jobsiteId),
    queryFn: () => jobsitesApi.fetchJobsiteActivity(jobsiteId),
    enabled: !!jobsiteId,
    staleTime: 60000,
    ...options,
  });
}

/**
 * Hook to fetch jobsite notifications
 */
export function useJobsiteNotifications(jobsiteId, options = {}) {
  return useQuery({
    queryKey: JOBSITES_KEYS.notifications(jobsiteId),
    queryFn: () => jobsitesApi.fetchJobsiteNotifications(jobsiteId),
    enabled: !!jobsiteId,
    staleTime: 60000,
    ...options,
  });
}

/**
 * Hook to fetch jobsite contractors
 */
export function useJobsiteContractors(jobsiteId, options = {}) {
  return useQuery({
    queryKey: JOBSITES_KEYS.contractors(jobsiteId),
    queryFn: () => jobsitesApi.fetchJobsiteContractors(jobsiteId),
    enabled: !!jobsiteId,
    staleTime: 60000,
    ...options,
  });
}

/**
 * Hook to fetch jobsite documents
 */
export function useJobsiteDocuments(jobsiteId, options = {}) {
  return useQuery({
    queryKey: JOBSITES_KEYS.documents(jobsiteId),
    queryFn: () => jobsitesApi.fetchJobsiteDocuments(jobsiteId),
    enabled: !!jobsiteId,
    staleTime: 60000,
    ...options,
  });
}

/**
 * Hook to fetch jobsite photos
 */
export function useJobsitePhotos(jobsiteId, options = {}) {
  return useQuery({
    queryKey: JOBSITES_KEYS.photos(jobsiteId),
    queryFn: () => jobsitesApi.fetchJobsitePhotos(jobsiteId),
    enabled: !!jobsiteId,
    staleTime: 60000,
    ...options,
  });
}

/**
 * Hook to fetch jobsite messages
 */
export function useJobsiteMessages(jobsiteId, options = {}) {
  return useQuery({
    queryKey: JOBSITES_KEYS.messages(jobsiteId),
    queryFn: () => jobsitesApi.fetchJobsiteMessages(jobsiteId),
    enabled: !!jobsiteId,
    staleTime: 60000,
    ...options,
  });
}

/**
 * Hook to fetch jobsite crews
 */
export function useJobsiteCrews(jobsiteId, options = {}) {
  return useQuery({
    queryKey: JOBSITES_KEYS.crews(jobsiteId),
    queryFn: () => jobsitesApi.fetchJobsiteCrews(jobsiteId),
    enabled: !!jobsiteId,
    staleTime: 60000,
    ...options,
  });
}

/**
 * Hook to fetch jobsite suggestions
 */
export function useJobsiteSuggestions(options = {}) {
  return useQuery({
    queryKey: JOBSITES_KEYS.suggestions(),
    queryFn: jobsitesApi.fetchJobsiteSuggestions,
    staleTime: 300000, // 5 minutes
    ...options,
  });
}

export default {
  useJobsites,
  useJobsite,
  useJobsiteSummary,
  useCreateJobsite,
  useUpdateJobsite,
  useDeleteJobsite,
  useJobsiteWorkers,
  useAcknowledgeJobsiteAlert,
  useJobsiteSafety,
  useJobsiteCompliance,
  useJobsiteActivity,
  useJobsiteNotifications,
  useJobsiteContractors,
  useJobsiteDocuments,
  useJobsitePhotos,
  useJobsiteMessages,
  useJobsiteCrews,
  useJobsiteSuggestions,
};
